import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check, Shield, Zap } from "lucide-react";
import { Mascot } from "@/components/Mascot";
import { Wordmark } from "@/components/Wordmark";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function loginWithGoogle() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const BENEFITS = [
  { c: "#FF8C42", t: "Feed multi-sources · refresh toutes les 2h" },
  { c: "#FFA85C", t: "Tendance quotidienne générée par Claude Sonnet 4.5" },
  { c: "#FFD600", t: "Récap hebdomadaire éditorial (lundi 07h)" },
  { c: "#4EA8FF", t: "Alertes en temps réel (experts + Core Updates)" },
  { c: "#E56E1F", t: "Timeline complète des Core Updates Google" },
  { c: "#FF8C42", t: "30+ experts internationaux + francophones" },
  { c: "#4EA8FF", t: "Veille dédiée SGE, ChatGPT, Perplexity" },
  { c: "#FFD600", t: "Favoris illimités + archives" },
];

const FAQ = [
  { q: "Puis-je résilier à tout moment ?", a: "Oui. Un clic, pas d'engagement, pas de frais." },
  { q: "Comment se fait le paiement ?", a: "Par carte via Stripe. La TVA est calculée automatiquement selon votre pays." },
  { q: "Ai-je besoin d'un compte Google ?", a: "Oui, la connexion se fait via Google (aucune inscription à remplir)." },
  { q: "Y a-t-il un essai gratuit ?", a: "Le feed est partiellement accessible sans compte (6 items). L'abonnement débloque tout immédiatement." },
];

export default function SubscriptionPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const goToCheckout = async () => {
    if (!user) {
      loginWithGoogle();
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen text-[#FAF0DE] relative overflow-hidden">
      <div className="mesh-bg" />

      <header className="relative z-10 max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="sub-back-link">
          <Mascot pose="waving" className="w-9 h-9" />
          <Wordmark className="h-5" text="#FAF0DE" accent="#FF8C42" />
        </Link>
        <Link to="/" className="text-xs font-mono uppercase tracking-widest text-[#FAF0DE]/60 hover:text-white flex items-center gap-2">
          <ArrowLeft className="w-3 h-3" /> Retour
        </Link>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 pb-24">
        <div className="text-center mb-16">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-[#FF8C42] mb-4">/ Abonnement</div>
          <h1
            className="font-display text-5xl md:text-8xl font-black tracking-[-0.03em] leading-[0.9] mb-6"
            data-testid="sub-page-title"
          >
            <span className="block">10€ / mois.</span>
            <span className="block text-shimmer">Tout inclus.</span>
          </h1>
          <p className="text-lg md:text-xl text-[#FAF0DE]/70 max-w-2xl mx-auto">
            Sans engagement. Résiliable à tout moment. TVA gérée automatiquement.
          </p>
        </div>

        <div className="grid md:grid-cols-12 gap-6 mb-16">
          {/* Mascot + trust */}
          <div className="md:col-span-5 space-y-6">
            <div className="rounded-3xl border border-[#FAF0DE]/10 bg-[#132038]/70 backdrop-blur p-8 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#FF8C42]/30 blur-3xl float-slow" />
              <Mascot pose="clipboard" className="w-40 h-40 mx-auto bob" />
              <div className="mt-4 text-center">
                <div className="font-display text-2xl font-black">Le radar allumé.</div>
                <div className="text-sm text-[#FAF0DE]/60 mt-2">
                  Vous vous abonnez, il commence à veiller.
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: Shield, t: "Stripe sécurisé", c: "#4EA8FF" },
                { icon: Zap, t: "Accès instant", c: "#FFD600" },
              ].map((b, i) => (
                <div key={i} className="rounded-2xl border border-[#FAF0DE]/10 bg-[#132038]/60 backdrop-blur p-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${b.c}18` }}>
                    <b.icon className="w-4 h-4" style={{ color: b.c }} />
                  </div>
                  <div className="text-sm font-medium">{b.t}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing card */}
          <div className="md:col-span-7">
            <div className="relative rounded-3xl border border-[#FF8C42]/40 bg-gradient-to-br from-[#1B2B48] to-[#0A1225] p-8 md:p-10 overflow-hidden">
              <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#FF8C42]/30 blur-3xl float-slow" />
              <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#4EA8FF]/20 blur-3xl float-med" />
              <div className="relative z-10">
                <div className="flex items-baseline gap-3 mb-1">
                  <span className="font-display text-7xl font-black text-shimmer">10€</span>
                  <span className="text-[#FAF0DE]/40 font-mono text-sm">/ mois</span>
                </div>
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FAF0DE]/50 mb-8">
                  Facturation mensuelle · résiliable en 1 clic
                </div>

                <div className="space-y-2.5 mb-10">
                  {BENEFITS.map((f, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm text-[#FAF0DE]/90">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ background: `${f.c}22`, border: `1px solid ${f.c}55` }}>
                        <Check className="w-3 h-3" style={{ color: f.c }} />
                      </div>
                      {f.t}
                    </div>
                  ))}
                </div>

                <button
                  onClick={goToCheckout}
                  disabled={loading}
                  data-testid="sub-checkout-btn"
                  className="w-full group inline-flex items-center justify-center gap-3 bg-[#FF8C42] text-[#0A1225] px-8 py-5 rounded-full font-mono text-sm uppercase tracking-widest hover:bg-[#FAF0DE] transition-all hover:scale-[1.02] shadow-[0_0_60px_-10px_rgba(255,140,66,0.7)] disabled:opacity-50"
                >
                  {loading ? "Redirection Stripe…" : user ? "S'abonner · 10€/mois" : "Se connecter puis s'abonner"}
                  <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
                </button>

                <p className="mt-4 text-center text-xs text-[#FAF0DE]/40 font-mono">
                  Paiement sécurisé par Stripe · TVA calculée automatiquement
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-[#FF8C42] mb-6 text-center">/ Questions fréquentes</div>
          <div className="grid md:grid-cols-2 gap-4">
            {FAQ.map((f, i) => (
              <div key={i} data-testid={`faq-${i}`} className="rounded-2xl border border-[#FAF0DE]/10 bg-[#132038]/50 backdrop-blur p-6">
                <div className="font-display text-lg font-bold mb-2">{f.q}</div>
                <div className="text-sm text-[#FAF0DE]/70 leading-relaxed">{f.a}</div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
